import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Accordion } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import { RootState } from 'store/reducers';
import { LayoutSelector } from 'store/selectors/selectors';
import { setIsShowEditAssetForm } from 'store/actions/layoutActions';
import { fetchAssistantProgress } from 'store/actions/assistantActions';
import { zvrRegistration } from 'store/actions/zvrActions';
import { temporaryFieldSaver } from 'store/actions/temporaryFieldSaverActions';
import { scrollToElementId } from 'modules/common/helpers';

import { Toggle } from 'modules/elements/components';
import { Modal } from 'modules/bootstrap/components';
import {
  AddAssetProcess,
  AssetTypePageFooter,
  AssetTypeRecommendations,
  CreatedAssets,
} from 'modules/assetType/components';
import { AssetForm } from 'modules/asset/components';

import { ASSET_CATEGORIES } from 'modules/estatePlan/constants';
import {
  InheritanceTaxWidget,
  NotarySearchWidget,
} from 'modules/estatePlan/components';
import { REDIRECT_ACTIONS } from 'modules/assistant/constants/questionnaires/questionnairesToAssetTypes';

interface AssetTypePageProps {
  assetCategory: string;
  categorizedRecos: RecommendationDTO[];
  possibleParams?: any[];
}

function AssetTypePage({
  assetCategory,
  categorizedRecos = [],
  possibleParams = [],
}: AssetTypePageProps) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation([assetCategory, 'plan']);
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('ref');

  const layoutSelector = useSelector((state: RootState) =>
    LayoutSelector(state)
  );
  const { currentAsset, currentAssetType, isShowEditForm } =
    layoutSelector.assetEdit;

  const assets: AssetsDTO = useSelector((state: RootState) => state.assets);
  const categoryAssets: AssetDTO[] = assets[assetCategory] || [];

  // filter all assets in the removal queue
  const temporaryField = useSelector(
    (state: RootState) => state.temporaryField?.value
  );
  const assetIdsToRemove =
    temporaryField?.type === 'AssetToRemove' ? temporaryField.assetIds : [];

  const displayedAssets = categoryAssets.filter(
    (asset) => !assetIdsToRemove.includes(asset.id)
  );

  // get data in case user gets redirected from other page
  const previousRouteData: any = location.state || {};
  const redirectAssetId =
    previousRouteData?.assetId || previousRouteData?.notificationData?.assetId;

  const dataOnRedirect = useSelector(
    (state: RootState) => state.temporaryField.value
  );

  // handle the redirect after successful payment of ZVR
  useEffect(() => {
    if (token) {
      handleZvrRegistration(token);
    }

    // if there is assetId from the params, scroll to the corresponding asset card
    if (possibleParams.length === 2) {
      scrollToElementId(possibleParams[1]);
    }
  }, []);

  useEffect(() => {
    // we need the assistant progress for the HCP & PD cards and fetch it here to avoid multiple requests
    if (assetCategory === ASSET_CATEGORIES.HEALTH) {
      dispatch(fetchAssistantProgress());
    }

    if (!possibleParams.length) {
      window.scrollTo(0, 0);
    }
  }, [assetCategory]);

  // if there is data on redirect, scroll to the corresponding asset card
  useEffect(() => {
    const assetIdToScroll = redirectAssetId || dataOnRedirect?.assetId;
    if (assetIdToScroll) {
      scrollToElementId(assetIdToScroll);
      dispatch(temporaryFieldSaver(null));
    }
  }, [redirectAssetId, dataOnRedirect]);

  const handleZvrRegistration = async (token: string) => {
    await dispatch(zvrRegistration(token));
    searchParams.delete('ref');
    setSearchParams(searchParams);
  };

  const handleCloseEditForm = () => {
    dispatch(setIsShowEditAssetForm(false));
    dispatch(temporaryFieldSaver(null));
  };

  const infoText = t(`plan:info_text_${assetCategory}`);

  const renderWidget = () => {
    switch (assetCategory) {
      case ASSET_CATEGORIES.FINANCIAL:
        return (
          <div id={REDIRECT_ACTIONS.GO_TO_TAX_CALCULATOR} className="t-mt-10">
            <InheritanceTaxWidget />
          </div>
        );

      case ASSET_CATEGORIES.LEGAL:
        return (
          <div className="t-mt-10">
            <NotarySearchWidget />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {isShowEditForm && (
        <Modal show className="small" style={{ zIndex: '1040' }}>
          <div className="t-bg-gamma-400 t-w-full t-shadow t-rounded t-p-8">
            <AssetForm
              asset={currentAsset}
              assetType={currentAssetType}
              assetCategory={assetCategory}
              onHideForm={handleCloseEditForm}
            />
          </div>
        </Modal>
      )}

      <div className="t-h-full t-flex t-flex-col t-justify-between">
        <div>
          <h2 className="t-font-secondary t-font-semibold t-text-xl t-pb-3">
            {t(`plan:${assetCategory}_page_title`)}
          </h2>
          <p className="t-hidden md:t-block t-mb-6">
            {t(`plan:${assetCategory}_page_description`)}
          </p>

          <Accordion>
            <Toggle
              eventKey={`${assetCategory}_learn_more`}
              title={t('plan:learn_more')}
              content={infoText}
            />
          </Accordion>

          <div className="t-mb-8" />

          <AssetTypeRecommendations
            category={assetCategory}
            recommendations={categorizedRecos}
          />

          <div className="t-mt-5" data-tour="vault_assets">
            <CreatedAssets
              assetCategory={assetCategory}
              categoryAssets={displayedAssets}
            />
          </div>

          {!!displayedAssets.length && (
            <AddAssetProcess assetCategory={assetCategory} />
          )}
        </div>

        <div>
          <AssetTypePageFooter currentCategory={assetCategory} />
          {renderWidget()}
        </div>
      </div>
    </>
  );
}

export default AssetTypePage;
